/**
 * /overview - Sites. The national map, edge to edge, and nothing else.
 *
 * A site appears only when the registry can run it: terrain on disk and either
 * a storage table or results of its own. A folder that carries neither is not
 * a study site yet, so it is not drawn.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import { SitesMap, type MapSite } from '../components/SitesMap'
import { useAppStore } from '../store/useAppStore'

interface SiteSummary {
  id: string
  display_name: string
  state: string | null
  river: string | null
  hazard_class: string
  india_region: string
  center: { lat: number; lon: number }
  reservoir_volume: { value: number | null; status: string }
  runnable?: boolean
  run_dir?: string | null
}

export default function Sites() {
  const navigate = useNavigate()
  const active = useAppStore((s) => s.activeSiteId)
  const setActive = useAppStore((s) => s.setActiveSiteId)
  const sites = useQuery<{ sites: SiteSummary[]; run_site: string }>({
    queryKey: ['sites'],
    queryFn: () => api.raw('/sites'),
  })

  const listed = (sites.data?.sites ?? []).filter((s) => s.runnable)
  const mapSites: MapSite[] = listed.map((s) => ({
    id: s.id,
    display_name: s.display_name,
    state: s.state,
    river: s.river,
    hazard_class: s.hazard_class,
    lat: s.center.lat,
    lon: s.center.lon,
    volume_m3: s.reservoir_volume.value,
    has_results: Boolean(s.run_dir) || s.id === sites.data?.run_site,
  }))

  const open = (id: string) => {
    setActive(id)
    navigate(`/site/${id}`)
  }

  if (sites.isPending) return <div className="skeleton m-4 h-[calc(100%-2rem)]" />

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="overview">
      <div className="flex shrink-0 items-baseline gap-4 border-b border-[var(--ink-border)] px-4 py-3">
        <h1 className="page-title">Sites</h1>
        <p className="text-14 text-slate-400">
          {listed.length} study {listed.length === 1 ? 'site' : 'sites'} · select a marker to open its dashboard
        </p>
      </div>
      <div className="min-h-0 flex-1">
        <SitesMap sites={mapSites} activeSiteId={active} onSelectSite={open} />
      </div>
    </div>
  )
}
